import { type NamedUser, userDisplayName } from './userDisplayName';

/**
 * Who a history record is attributed to, once that user may have been deleted (SKU-1241).
 *
 * Mirrors `packages/api/src/user/attribution.ts`. Customer comments (and the other attributed
 * history rows) are KEPT when their user is deleted: the relation is cleared and the record
 * carries a name snapshot taken when it was written. The record is never hidden; its author is
 * shown by that snapshot, explicitly marked as a deleted user.
 */
export const DELETED_USER_MARKER = '(deleted user)';

/** Neither a live user nor a snapshot — visible, never blank. */
export const DELETED_USER_UNRECORDED = 'Deleted user (name not recorded)';

export const attributedUserLabel = (
  live: NamedUser | null | undefined,
  nameSnapshot: string | null | undefined,
): string => {
  if (live) return userDisplayName(live);
  if (nameSnapshot) return `${nameSnapshot} ${DELETED_USER_MARKER}`;
  return DELETED_USER_UNRECORDED;
};

/**
 * A support-ticket participant (submitter, reply author, FAQ author). Once that person's user has
 * been deleted the server sends `deletedUserLabel` — the recorded name, already marked
 * "(deleted user)" — with null name parts; otherwise it is null and the name parts are shown.
 */
export interface ParticipantLike extends NamedUser {
  deletedUserLabel?: string | null;
}

export const participantDisplayName = (participant: ParticipantLike): string =>
  participant.deletedUserLabel ?? userDisplayName(participant);
